import * as Sentry from "@sentry/react";

// loadLink gives null for a link that's gone (a 403 or 404), so there's no error to read a status from.
const GONE = Object.freeze({ gone: true });

// What a failure's line names as its cause; "no response" is only a request that was sent and never answered.
export function failureCause(step, cause) {
  if (step === "build") return "pdf";
  if (cause === GONE) return "gone";
  if (cause?.response?.status) return cause.response.status;
  if (Number.isInteger(cause?.status) && cause.status >= 100 && cause.status <= 599) return cause.status;
  return cause?.request ? "no response" : "unexpected error";
}

// One line per failure with the HTTP status at most, since the error, its request config and its URL carry the link's secret.
export const failureLine = (step, label, cause) => `[sign-all] ${step} failed for ${label}: ${failureCause(step, cause)}`;

// Sentry keeps no console breadcrumbs, so the line also goes to it as a warning of its own.
export function logFailure(step, label, cause) {
  // 1156: CP documents are switched off. An expected state, shown to the signer, not a failure to report.
  if (cause?.code === 1156) return;
  const line = failureLine(step, label, cause);
  console.warn(line);
  Sentry.captureMessage(line, "warning");
}

// A signing step's failure: the reason the signer sees, with the step's name and what it caught kept for the log line.
export const failedStep = (step, reason, cause) => Object.assign(new Error(reason, { cause }), { step });

// ENG-1865: signs one agreement at a time, in order, and keeps going past a failure.
// Each agreement is re-read first, so a submit that went through but lost its response is never signed twice.
export async function signInOrder(agreements, { reload, signOne }, onUpdate) {
  for (const agreement of agreements) {
    onUpdate(agreement.secret, { status: "signing" });
    try {
      let fresh;
      try {
        fresh = await reload(agreement.secret);
      } catch (cause) {
        throw failedStep("load", "Couldn't reach the server", cause);
      }
      if (!fresh) throw failedStep("load", "This agreement is no longer available", GONE);
      if (fresh.status === 2) {
        onUpdate(agreement.secret, { status: "signed", already: true });
        continue;
      }
      await signOne(agreement, fresh);
      onUpdate(agreement.secret, { status: "signed" });
    } catch (error) {
      logFailure(error?.step ?? "sign", agreement.label, error?.step ? error.cause : error);
      onUpdate(agreement.secret, { status: "failed", reason: error?.message || "Something went wrong" });
    }
  }
}
